// Phase-0 spike — mic capture (S3).
//
// getUserMedia -> AudioWorklet -> 16-bit PCM frames + an RMS level. The worklet
// runs in its own global scope, so we ship it as a Blob URL string rather than a
// separate bundled file — this sidesteps every bundler/worklet-module pitfall
// and keeps the whole spike in `src/spike/`.
//
// We capture at whatever rate the AudioContext gives us (we *request* 16 kHz,
// which Chrome honours) and report that rate so the provider config matches —
// no client-side resampling needed.

const WORKLET_SOURCE = `
class PCMCapture extends AudioWorkletProcessor {
  constructor() {
    super()
    this.frame = 2048
    this.acc = new Float32Array(this.frame)
    this.idx = 0
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0]
    if (!ch) return true
    for (let i = 0; i < ch.length; i++) {
      this.acc[this.idx++] = ch[i]
      if (this.idx === this.frame) {
        this.flush()
        this.idx = 0
      }
    }
    return true
  }
  flush() {
    const out = new Int16Array(this.frame)
    let sum = 0
    for (let i = 0; i < this.frame; i++) {
      const s = Math.max(-1, Math.min(1, this.acc[i]))
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      sum += this.acc[i] * this.acc[i]
    }
    const rms = Math.sqrt(sum / this.frame)
    this.port.postMessage({ pcm: out.buffer, rms }, [out.buffer])
  }
}
registerProcessor('pcm-capture', PCMCapture)
`

export interface MicCapture {
  /** Begins capture. Resolves with the actual sample rate of the stream. */
  start: (
    onFrame: (pcm: ArrayBuffer) => void,
    onLevel: (rms: number) => void
  ) => Promise<number>
  stop: () => Promise<void>
}

interface FramePayload {
  pcm: ArrayBuffer
  rms: number
}

export function createMicCapture(deviceId?: string): MicCapture {
  let ctx: AudioContext | null = null
  let stream: MediaStream | null = null
  let node: AudioWorkletNode | null = null
  let workletUrl: string | null = null

  async function start(
    onFrame: (pcm: ArrayBuffer) => void,
    onLevel: (rms: number) => void
  ): Promise<number> {
    ctx = new AudioContext({ sampleRate: 16000 })
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: deviceId ? { exact: deviceId } : undefined,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    })

    workletUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: 'application/javascript' })
    )
    await ctx.audioWorklet.addModule(workletUrl)

    const source = ctx.createMediaStreamSource(stream)
    node = new AudioWorkletNode(ctx, 'pcm-capture')
    node.port.onmessage = (e: MessageEvent<FramePayload>) => {
      onFrame(e.data.pcm)
      onLevel(e.data.rms)
    }
    // Connect through to the destination so the graph keeps pulling audio; the
    // worklet writes no output, so nothing is played back (no room feedback).
    source.connect(node)
    node.connect(ctx.destination)

    return ctx.sampleRate
  }

  async function stop(): Promise<void> {
    node?.port.close()
    node?.disconnect()
    stream?.getTracks().forEach((t) => t.stop())
    if (ctx) await ctx.close()
    if (workletUrl) URL.revokeObjectURL(workletUrl)
    ctx = null
    stream = null
    node = null
    workletUrl = null
  }

  return { start, stop }
}

/** List available audio input devices. Labels require a prior permission grant. */
export async function listMics(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices()
  return devices.filter((d) => d.kind === 'audioinput')
}
